import * as React from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  FloatingArrow,
  hide,
  safePolygon
} from '@floating-ui/react';
import type { Placement, SideObject } from '@floating-ui/react';
import { arrow } from '@floating-ui/dom';
import { RefObject, useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './TooltipV2.module.scss';

type HideStrategy = 'escaped' | 'referenceHidden' | undefined;

interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  arrowRef?: RefObject<any>;
  hiddenPadding?: number | Partial<SideObject>;
  tooltipGap?: number;
  hideStrategy?: HideStrategy;
  mainAxisOffset?: number;
  crossAxisOffset?: number;
}

interface TooltipContentProps extends React.HTMLProps<HTMLDivElement> {
  arrowClassName?: string;
  arrowColor?: string;
  bgColor?: string;
  shouldAnimate?: boolean;
}

export enum Directions {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  NONE = 'none'
}

export function useTooltip({
  initialOpen = false,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  arrowRef,
  hiddenPadding = 16,
  tooltipGap = 7,
  hideStrategy = 'escaped',
  mainAxisOffset = 0,
  crossAxisOffset = 0
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(tooltipGap),
      offset({
        mainAxis: mainAxisOffset,
        crossAxis: crossAxisOffset
      }),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef?.current }),
      hide({
        padding: hiddenPadding,
        strategy: hideStrategy
      })
    ]
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    handleClose: safePolygon()
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null
  });
  const dismiss = useDismiss(context, {
    referencePress: true,
    outsidePress: true
  });

  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data
    }),
    [open, setOpen, interactions, data]
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = React.createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({ children, ...options }: { children: React.ReactNode } & TooltipOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const arrowRef = useRef(null);
  options.arrowRef = arrowRef;
  const tooltip = useTooltip(options);
  //@ts-expect-error adding arrow ref to tooltip
  tooltip.arrowRef = arrowRef;

  return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

export const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & {
    asChild?: boolean;
    tooltipTriggerClassName?: string;
    ellipsis?: boolean;
  }
>(function TooltipTrigger(
  { children, asChild = false, tooltipTriggerClassName, ...props },
  propRef
) {
  const context = useTooltipContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
        className: cx({
          tooltipTriggerClassName: true,
          [children.props.className]: true,
          ['text-ellipsis overflow-hidden whitespace-nowrap']: props.ellipsis
        })
      })
    );
  }

  return (
    <span
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
      className={tooltipTriggerClassName}
    >
      {children}
    </span>
  );
});

export const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  //eslint-disable-next-line
  function TooltipContent({ style, ...props }, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    const referenceHidden =
      context?.middlewareData?.hide?.referenceHidden || context?.middlewareData?.hide?.escaped;

    const arrowColor = props?.arrowColor || 'var(--tooltip-background-color)';
    const bgColor = props?.bgColor || 'var(--tooltip-background-color)';
    const arrowClass = props?.arrowClassName || '';
    const shouldAnimate = props?.shouldAnimate || false;

    if (!context.open) return null;

    return (
      <FloatingPortal>
        <div
          ref={ref}
          style={{
            ...context.floatingStyles,
            ...style,
            background: bgColor,
            visibility: referenceHidden ? 'hidden' : 'visible'
          }}
          {...context.getFloatingProps(props)}
        >
          {shouldAnimate ? (
            <div className={styles.tooltipContent}>{props.children}</div>
          ) : (
            props.children
          )}
          {/* @ts-expect-error typescript is not able to infer the type of arrowRef */}
          <FloatingArrow
            className={arrowClass}
            context={context}
            ref={context?.arrowRef}
            fill={arrowColor}
          />
        </div>
      </FloatingPortal>
    );
  }
);
